import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { Alert } from '../../design/primitives/Alert';
import styles from './dashboard.module.css';

/**
 * O que um cartao da home pode dizer — e nada alem disso.
 *
 * "Nada precisa da sua atencao agora" e uma afirmacao sobre o **servidor**: so
 * quem viu a resposta tem como faze-la. Dita enquanto a consulta corre, ela e um
 * palpite; dita no lugar de um erro, ela troca falha por calma. Foi assim que a
 * home anunciou tranquilidade a um aluno que tinha aula marcada.
 *
 * A correcao recusada foi acrescentar `!isPending && !isError` em cada cartao:
 * sao tres telas, tres oportunidades de esquecer um dos dois, e o esquecimento
 * nao quebra nada visivel — ele so mente. O que esta escrito aqui e mais forte:
 * o vazio existe apenas como valor que `panelState` produz **depois** de olhar
 * `data`, e a marca abaixo, que este modulo nao exporta, impede qualquer tela de
 * montar `{ kind: 'empty' }` a mao.
 */
declare const producedByPanelState: unique symbol;

interface Sealed {
  readonly [producedByPanelState]: true;
}

export type PanelState<T> =
  | (Sealed & { readonly kind: 'loading' })
  | (Sealed & { readonly kind: 'failed' })
  | (Sealed & { readonly kind: 'empty' })
  | (Sealed & { readonly kind: 'ready'; readonly value: T });

/** O recorte de `useQuery` que este modulo consome, e so ele. */
export interface PanelQuery<TData> {
  readonly isError: boolean;
  readonly data: TData | undefined;
}

type Unsealed<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'failed' }
  | { readonly kind: 'empty' }
  | { readonly kind: 'ready'; readonly value: T };

function seal<T>(state: Unsealed<T>): PanelState<T> {
  return state as PanelState<T>;
}

/**
 * Deriva o estado do cartao da consulta. `select` devolve `undefined` para dizer
 * "a resposta chegou e nao ha o que mostrar" — e essa e a unica forma de dize-lo.
 *
 * A ordem dos testes e o conteudo da regra: erro primeiro, porque a resposta que
 * nao veio nao e uma resposta vazia; depois `data === undefined`, que cobre a
 * primeira carga **e** a consulta ainda desligada por falta de `me`, dois casos
 * que `isPending` sozinho nao separa de nada.
 */
export function panelState<TData, TValue>(
  query: PanelQuery<TData>,
  select: (data: TData) => TValue | undefined,
): PanelState<TValue> {
  if (query.isError) return seal<TValue>({ kind: 'failed' });
  if (query.data === undefined) return seal<TValue>({ kind: 'loading' });

  const value = select(query.data);
  return value === undefined ? seal<TValue>({ kind: 'empty' }) : seal<TValue>({ kind: 'ready', value });
}

export interface PanelSectionProps<T> {
  readonly id: string;
  readonly title: string;
  readonly state: PanelState<T>;
  /**
   * A frase do vazio. Obrigatoria: cartao que pode ficar vazio sem ter o que
   * dizer deixa um retangulo mudo, e retangulo mudo se le como carga travada.
   */
  readonly empty: string;
  readonly children: (value: T) => ReactNode;
}

export function PanelSection<T>({ id, title, state, empty, children }: PanelSectionProps<T>) {
  const { t } = useTranslation();

  return (
    <section className={styles.section} aria-labelledby={id}>
      <h2 id={id} className={styles.sectionTitle}>
        {title}
      </h2>

      {state.kind === 'loading' ? <p className={styles.empty}>{t('app.loading')}</p> : null}
      {state.kind === 'failed' ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}
      {state.kind === 'empty' ? <p className={styles.empty}>{empty}</p> : null}
      {state.kind === 'ready' ? children(state.value) : null}
    </section>
  );
}
